export const PLAYER_NICKNAME_KEY = 'kuogam.player-nickname';
export const LEADERBOARD_KEY = 'kuogam.local-leaderboard';

export type LeaderboardEntry = {
  nickname: string;
  score: number;
  kills: number;
  level: number;
  survivedSeconds: number;
  victory: boolean;
  recordedAt: string;
};

export type RunResult = Omit<LeaderboardEntry, 'nickname' | 'score' | 'recordedAt'>;

export function sanitizeNickname(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^0-9A-Za-zА-Яа-яЁё _-]/g, '')
    .slice(0, 16)
    .trim();
}

export function calculateRunScore(result: RunResult): number {
  const survival = Math.max(0, Math.floor(result.survivedSeconds));
  const kills = Math.max(0, Math.floor(result.kills));
  const level = Math.max(1, Math.floor(result.level));
  return survival + kills * 10 + (level - 1) * 35 + (result.victory ? 300 : 0);
}

export function createLeaderboardEntry(
  nickname: string,
  result: RunResult,
  recordedAt = new Date().toISOString(),
): LeaderboardEntry {
  return {
    nickname: sanitizeNickname(nickname),
    ...result,
    score: calculateRunScore(result),
    recordedAt,
  };
}

function compareEntries(left: LeaderboardEntry, right: LeaderboardEntry): number {
  return right.score - left.score
    || right.kills - left.kills
    || right.survivedSeconds - left.survivedSeconds
    || left.recordedAt.localeCompare(right.recordedAt);
}

export function mergeLeaderboardEntry(
  entries: readonly LeaderboardEntry[],
  candidate: LeaderboardEntry,
  limit = 10,
): LeaderboardEntry[] {
  const bestByNickname = new Map<string, LeaderboardEntry>();
  [...entries, candidate].forEach((entry) => {
    if (!entry.nickname) return;
    const key = entry.nickname.toLocaleLowerCase('ru');
    const previous = bestByNickname.get(key);
    if (!previous || compareEntries(entry, previous) < 0) bestByNickname.set(key, entry);
  });
  return [...bestByNickname.values()].sort(compareEntries).slice(0, Math.max(1, limit));
}

export function loadNickname(storage: Storage): string | null {
  try {
    const nickname = sanitizeNickname(storage.getItem(PLAYER_NICKNAME_KEY) ?? '');
    return nickname || null;
  } catch {
    return null;
  }
}

export function saveNickname(storage: Storage, nickname: string): string | null {
  const sanitized = sanitizeNickname(nickname);
  if (sanitized.length < 2) return null;
  try {
    storage.setItem(PLAYER_NICKNAME_KEY, sanitized);
  } catch {
    return null;
  }
  return sanitized;
}

export function loadLeaderboard(storage: Storage): LeaderboardEntry[] {
  try {
    const parsed = JSON.parse(storage.getItem(LEADERBOARD_KEY) ?? '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry): entry is LeaderboardEntry => (
        typeof entry === 'object'
        && entry !== null
        && typeof (entry as LeaderboardEntry).nickname === 'string'
        && typeof (entry as LeaderboardEntry).score === 'number'
        && typeof (entry as LeaderboardEntry).kills === 'number'
        && typeof (entry as LeaderboardEntry).level === 'number'
        && typeof (entry as LeaderboardEntry).survivedSeconds === 'number'
        && typeof (entry as LeaderboardEntry).victory === 'boolean'
        && typeof (entry as LeaderboardEntry).recordedAt === 'string'
      ))
      .sort(compareEntries)
      .slice(0, 10);
  } catch {
    return [];
  }
}

export function recordRunResult(
  storage: Storage,
  nickname: string,
  result: RunResult,
): LeaderboardEntry[] {
  const entry = createLeaderboardEntry(nickname, result);
  const leaderboard = mergeLeaderboardEntry(loadLeaderboard(storage), entry);
  try {
    storage.setItem(LEADERBOARD_KEY, JSON.stringify(leaderboard));
  } catch {
    return leaderboard;
  }
  return leaderboard;
}
